import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Lock,
  LogOut,
  Mail,
  Phone,
  User,
  Users,
  Sparkles,
  RefreshCw,
  ShieldCheck,
  ShieldAlert,
  Inbox,
  Calendar,
  ArrowLeft,
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ADMIN } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "bba_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(false);

  useEffect(() => {
    if (!token) return;
    // verify token by pinging summary
    axios
      .get(`${API}/admin/summary`, { headers: { "X-Admin-Token": token } })
      .then(() => setAuthed(true))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setAuthed(false);
      });
  }, [token]);

  const onLogin = (t) => {
    localStorage.setItem(TOKEN_KEY, t);
    setToken(t);
    setAuthed(true);
  };

  const onLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
  };

  if (!authed) return <AdminLogin onLogin={onLogin} />;
  return <AdminDashboard token={token} onLogout={onLogout} />;
}

function AdminLogin({ onLogin }) {
  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!value) {
      setError("Enter your admin token.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/login`, { token: value });
      onLogin(value);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Login failed.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#050B14] text-white font-body flex items-center justify-center px-6">
      <form
        data-testid={ADMIN.loginForm}
        onSubmit={submit}
        className="bba-card w-full max-w-md p-8 sm:p-10 space-y-6"
      >
        <div>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-[#93A5C0] hover:text-white transition-colors"
          >
            <ArrowLeft size={13} /> Back to site
          </Link>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 grid place-items-center border border-white/25">
            <Lock size={18} className="text-white" />
          </div>
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#93A5C0]">
              Coach Access
            </div>
            <h1 className="font-display text-2xl tracking-tight">
              BBA Admin
            </h1>
          </div>
        </div>

        <div>
          <label className="text-[11px] tracking-[0.22em] uppercase text-white/60">
            Admin Token
          </label>
          <input
            type="password"
            autoFocus
            data-testid={ADMIN.loginInput}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Enter your admin token"
            className="bba-input w-full mt-2 px-4 py-3 text-sm"
          />
        </div>

        {error ? (
          <div
            data-testid={ADMIN.loginError}
            className="text-sm text-red-300 bg-red-500/10 border border-red-400/30 px-3 py-2"
          >
            {error}
          </div>
        ) : null}

        <button
          type="submit"
          disabled={submitting}
          data-testid={ADMIN.loginSubmit}
          className="btn-gold w-full px-6 py-3.5 text-sm uppercase disabled:opacity-60"
        >
          {submitting ? "Signing in..." : "Sign In"}
        </button>

        <p className="text-xs text-[#A0ABC0] leading-relaxed">
          The admin token is stored in <code className="text-white">backend/.env</code> as{" "}
          <code className="text-white">ADMIN_TOKEN</code>. Change it there and restart the
          backend to update.
        </p>
      </form>
    </main>
  );
}

function AdminDashboard({ token, onLogout }) {
  const [tab, setTab] = useState("interest");
  const [summary, setSummary] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [interest, setInterest] = useState([]);
  const [loading, setLoading] = useState(false);

  const headers = { "X-Admin-Token": token };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c, i] = await Promise.all([
        axios.get(`${API}/admin/summary`, { headers }),
        axios.get(`${API}/admin/contacts`, { headers }),
        axios.get(`${API}/admin/player-interest`, { headers }),
      ]);
      setSummary(s.data);
      setContacts(c.data);
      setInterest(i.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load inquiries.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <main
      data-testid={ADMIN.dashboard}
      className="min-h-screen bg-[#050B14] text-white font-body"
    >
      <header className="border-b border-white/10 bg-[#0A1128]/70 backdrop-blur-xl sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/brand/bba-logo.png"
              alt="Team BBA Elite"
              className="h-9 w-auto"
            />
            <div className="leading-tight">
              <div className="font-display text-sm">BBA · ADMIN</div>
              <div className="text-[9px] tracking-[0.3em] text-[#93A5C0]">
                Inquiries Console
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={load}
              data-testid={ADMIN.refresh}
              className="inline-flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-wider text-white/80 hover:text-white border border-white/15 hover:border-white/40"
            >
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
            <Link
              to="/"
              className="text-xs uppercase tracking-wider text-white/60 hover:text-white"
            >
              View site
            </Link>
            <button
              type="button"
              onClick={onLogout}
              data-testid={ADMIN.logout}
              className="inline-flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-wider text-white/80 hover:text-white border border-white/15 hover:border-white/40"
            >
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <StatCard
            icon={Users}
            label="Player Interest"
            value={summary?.player_interest_total ?? "—"}
            testId={ADMIN.summaryInterest}
          />
          <StatCard
            icon={Inbox}
            label="Contact Inquiries"
            value={summary?.contacts_total ?? "—"}
            testId={ADMIN.summaryContacts}
          />
          <StatCard
            icon={Sparkles}
            label="Sponsorship Requests"
            value={summary?.sponsorship_total ?? "—"}
            testId={ADMIN.summarySponsorship}
          />
          <div
            data-testid={ADMIN.emailStatus}
            className="bba-card p-5"
            title="Email alerts status"
          >
            <div className="flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#93A5C0]">
              <Mail size={12} /> Email Alerts
            </div>
            <div className="mt-3 flex items-center gap-2 text-lg font-display">
              {summary?.email_alerts_enabled ? (
                <>
                  <ShieldCheck size={18} className="text-emerald-300" />
                  <span>Enabled</span>
                </>
              ) : (
                <>
                  <ShieldAlert size={18} className="text-amber-300" />
                  <span>Not configured</span>
                </>
              )}
            </div>
            <div className="text-[11px] text-[#A0ABC0] mt-2 leading-relaxed">
              {summary?.email_alerts_enabled
                ? "Resend is active. Alerts will send on new inquiries."
                : "Set RESEND_API_KEY in backend/.env to enable."}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 border-b border-white/10">
          <TabButton
            active={tab === "interest"}
            onClick={() => setTab("interest")}
            testId={ADMIN.tabInterest}
          >
            Player Interest ({interest.length})
          </TabButton>
          <TabButton
            active={tab === "contacts"}
            onClick={() => setTab("contacts")}
            testId={ADMIN.tabContacts}
          >
            Contact ({contacts.length})
          </TabButton>
        </div>

        <div className="mt-8">
          {tab === "interest" ? (
            <div data-testid={ADMIN.interestList} className="space-y-4">
              {interest.length === 0 ? (
                <EmptyState label="No player interest submissions yet." />
              ) : (
                interest.map((item) => (
                  <InterestCard key={item.id} item={item} />
                ))
              )}
            </div>
          ) : (
            <div data-testid={ADMIN.contactsList} className="space-y-4">
              {contacts.length === 0 ? (
                <EmptyState label="No contact inquiries yet." />
              ) : (
                contacts.map((item) => (
                  <ContactCard key={item.id} item={item} />
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}

const StatCard = ({ icon: Icon, label, value, testId }) => (
  <div data-testid={testId} className="bba-card p-5">
    <div className="flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#93A5C0]">
      <Icon size={12} /> {label}
    </div>
    <div className="mt-3 font-display text-3xl">{value}</div>
  </div>
);

const TabButton = ({ active, onClick, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`px-4 py-3 text-xs tracking-[0.2em] uppercase border-b-2 -mb-px transition-colors ${
      active
        ? "border-white text-white"
        : "border-transparent text-white/50 hover:text-white/80"
    }`}
  >
    {children}
  </button>
);

const EmptyState = ({ label }) => (
  <div className="bba-card p-10 text-center text-[#A0ABC0]">
    <Inbox size={22} className="mx-auto text-[#93A5C0]" />
    <div className="mt-3 text-sm">{label}</div>
  </div>
);

const fieldRow = (Icon, label, value) => {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3">
      <Icon size={14} className="text-[#93A5C0] mt-1 flex-shrink-0" />
      <div className="min-w-0">
        <div className="text-[10px] tracking-[0.22em] uppercase text-[#93A5C0]">
          {label}
        </div>
        <div className="text-sm text-white break-words">{value}</div>
      </div>
    </div>
  );
};

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
};

const InterestCard = ({ item }) => (
  <article className="bba-card p-6 sm:p-7">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <div className="font-display text-xl tracking-tight">
          {item.player_name}
        </div>
        {item.parent_name ? (
          <div className="text-xs text-[#A0ABC0] mt-1">
            Parent · {item.parent_name}
          </div>
        ) : null}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {item.sponsorship_interest ? (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-[10px] tracking-[0.2em] uppercase border border-[#B8C5D6] text-[#B8C5D6]">
            <Sparkles size={11} /> Sponsorship
          </span>
        ) : null}
        <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase text-[#93A5C0]">
          <Calendar size={11} /> {formatDate(item.created_at)}
        </span>
      </div>
    </div>

    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-5">
      {fieldRow(Mail, "Email", (
        <a href={`mailto:${item.email}`} className="hover:text-[#B8C5D6]">
          {item.email}
        </a>
      ))}
      {fieldRow(Phone, "Phone", item.phone)}
      {fieldRow(User, "Grade / Age", item.grade_age)}
      {fieldRow(User, "Position", item.position)}
      {fieldRow(Users, "Current Team", item.current_team)}
    </div>

    <div className="mt-5 border-l-2 border-[#B8C5D6]/60 pl-4 py-1">
      <div className="text-[10px] tracking-[0.22em] uppercase text-[#93A5C0] mb-1">
        Message
      </div>
      <p className="text-sm text-white/90 whitespace-pre-wrap leading-relaxed">
        {item.message}
      </p>
    </div>
  </article>
);

const ContactCard = ({ item }) => (
  <article className="bba-card p-6 sm:p-7">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <div className="font-display text-xl tracking-tight">{item.name}</div>
        {item.program_interest ? (
          <div className="text-xs text-[#A0ABC0] mt-1">
            Interest · {item.program_interest}
          </div>
        ) : null}
      </div>
      <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase text-[#93A5C0]">
        <Calendar size={11} /> {formatDate(item.created_at)}
      </span>
    </div>

    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-5">
      {fieldRow(Mail, "Email", (
        <a href={`mailto:${item.email}`} className="hover:text-[#B8C5D6]">
          {item.email}
        </a>
      ))}
      {fieldRow(Phone, "Phone", item.phone)}
      {fieldRow(User, "Player Age / Grade", item.player_age)}
    </div>

    <div className="mt-5 border-l-2 border-[#B8C5D6]/60 pl-4 py-1">
      <div className="text-[10px] tracking-[0.22em] uppercase text-[#93A5C0] mb-1">
        Message
      </div>
      <p className="text-sm text-white/90 whitespace-pre-wrap leading-relaxed">
        {item.message}
      </p>
    </div>
  </article>
);
